/**
 * InstallAppButton — surfaces the browser's 'Install' prompt when available.
 *
 * Chrome / Edge / Android / Desktop Firefox fire a `beforeinstallprompt` event
 * that lets us defer the native UI and trigger it on user click.
 *
 * iOS Safari doesn't support beforeinstallprompt — the user has to use 'Add
 * to Home Screen' from the share sheet. We detect standalone/installed mode
 * and hide the button in those cases.
 */
import { useEffect, useState } from 'react'
import { Download, Check } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function InstallAppButton() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(false)

  useEffect(() => {
    // Already running as installed PWA
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS-specific non-standard property — cast via any
      (window.navigator as unknown as { standalone?: boolean }).standalone === true
    if (isStandalone) {
      setInstalled(true)
      return
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const handleInstalled = () => {
      setInstalled(true)
      setDeferred(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled', handleInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleInstalled)
    }
  }, [])

  if (installed) {
    return (
      <span
        className="flex items-center gap-1.5 text-[10px] text-emerald-400 opacity-70"
        title="Running as installed app"
      >
        <Check size={10} />
        Installed
      </span>
    )
  }

  if (!deferred) {
    // iOS fallback hint — the only path on iPhone/iPad is manual
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !(window as any).MSStream
    if (isIOS) {
      return (
        <span
          className="text-[10px] text-text-muted hover:text-text-primary cursor-help"
          title="On iOS: tap Share → 'Add to Home Screen' to install"
        >
          📱 Install (iOS)
        </span>
      )
    }
    return null
  }

  const handleInstall = async () => {
    try {
      await deferred.prompt()
      const { outcome } = await deferred.userChoice
      if (outcome === 'accepted') {
        setInstalled(true)
      }
      setDeferred(null)
    } catch {
      setDeferred(null)
    }
  }

  return (
    <button
      type="button"
      onClick={handleInstall}
      title="Install FinanceGenie as an app on this device"
      className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
    >
      <Download size={12} />
      Install App
    </button>
  )
}
